// -----------------------------------------------------------------------------
// PRINTER — puts PDF pages into a hidden print area and opens the browser's print box (Part 5).
//   await printPdf("2026-09-26 LZ-Low zone level 14–43-V02", <ComponentPdf data={…} ctx={…} />)
// Steps: render into <div class="print-root"> (portal in <body>, off screen) → wait until pages stop measuring
// (data-measuring) and pictures are decoded → document.title = file name (the print box suggests it) →
// window.print() → restore the title and empty the print area.
// The CSS that hides the app while printing and sets A4 portrait pages is in public/styles.css ("PRINT").
// PrintHost is mounted once in App.tsx. Tests replace window.print (tests/e2e/smoke.mjs counts the calls).
// -----------------------------------------------------------------------------
import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

let current: { node: ReactNode; key: number } | null = null;
const listeners = new Set<(v: typeof current) => void>();
const emit = () => listeners.forEach((l) => l(current));

const frame = () => new Promise<void>((r) => requestAnimationFrame(() => r()));

/** Wait for the print area to finish measuring and for its pictures to decode (max ~3 s). */
async function settled(): Promise<HTMLElement> {
  for (let k = 0; k < 150; k++) {
    await frame();
    const root = document.querySelector<HTMLElement>(".print-root");
    if (root && root.childElementCount && !root.querySelector("[data-measuring]")) {
      await Promise.all([...root.querySelectorAll("img")].map((img) => img.decode().catch(() => undefined)));
      const pages = root.querySelectorAll(".pp");
      pages[pages.length - 1]?.setAttribute("data-last", "1");   // no page break after the last page (no blank page)
      return root;
    }
  }
  throw new Error("The pages could not be prepared for printing");
}

let busy = false;
/** Open the print box for these pages; `title` becomes the suggested file name. Resolves when the box closes. */
export async function printPdf(title: string, node: ReactNode): Promise<void> {
  if (busy) return;
  busy = true;
  const before = document.title;
  try {
    current = { node, key: Date.now() }; emit();
    await settled();
    document.title = title;
    window.print();          // blocks until the print box closes in Chrome / Edge
    await frame();
  } finally {
    document.title = before;
    current = null; emit();
    busy = false;
  }
}

export function PrintHost() {
  const [v, setV] = useState(current);
  useEffect(() => { listeners.add(setV); return () => { listeners.delete(setV); }; }, []);
  if (!v) return null;
  return createPortal(<div className="print-root" key={v.key} aria-hidden="true">{v.node}</div>, document.body);
}
